/**
 * horariosSalaUtil.ts — Utilidades del modelo V2 de horarios.
 *
 * Reemplaza las lecturas legacy de `sala.horario` y `sala.diasAtencion`
 * por consumo de la subcolección `salas/{salaId}/horarios` (reglas
 * recurrentes con vigencia).
 */
import type { Timestamp } from 'firebase/firestore';

export interface ReglaHorario {
  id: string;
  diaSemana: number; // 0=Dom, 6=Sáb
  horaInicio: string; // "HH:mm"
  horaFin: string; // "HH:mm"
  vigenciaDesde: Timestamp;
  vigenciaHasta: Timestamp | null;
  prioridad?: number;
}

const PANAMA_TZ = 'America/Panama';

/** Returns current HH:mm in Panama timezone. */
export function currentHhmmPanama(): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: PANAMA_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date());
}

/** Returns current weekday in Panama (0=Sun, 6=Sat). */
export function currentDowPanama(): number {
  const weekday =
    new Intl.DateTimeFormat('en-US', {
      timeZone: PANAMA_TZ,
      weekday: 'short',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'weekday')?.value ?? 'Sun';
  return (
    ({ Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 } as const)[
      weekday as 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'
    ] ?? 0
  );
}

/** Filtra las reglas vigentes para HOY (Panamá). */
export function reglasVigentesHoy(reglas: ReglaHorario[]): ReglaHorario[] {
  const ahora = Date.now();
  const dow = currentDowPanama();
  return reglas.filter((r) => {
    if (r.diaSemana !== dow) return false;
    if (r.vigenciaDesde.toMillis() > ahora) return false;
    if (r.vigenciaHasta && r.vigenciaHasta.toMillis() < ahora) return false;
    return true;
  });
}

/** Devuelve true si la sala está abierta AHORA según sus reglas. */
export function isSalaAbiertaAhora(reglas: ReglaHorario[]): boolean {
  const hhmm = currentHhmmPanama();
  return reglasVigentesHoy(reglas).some(
    (r) => hhmm >= r.horaInicio && hhmm <= r.horaFin,
  );
}

/** Minutos hasta el cierre del bloque ACTUAL. Infinity si no hay bloque activo. */
export function minutosHastaCierre(reglas: ReglaHorario[]): number {
  const hhmm = currentHhmmPanama();
  const bloque = reglasVigentesHoy(reglas).find(
    (r) => hhmm >= r.horaInicio && hhmm <= r.horaFin,
  );
  if (!bloque) return Infinity;
  const [h, m] = bloque.horaFin.split(':').map(Number);
  const [hN, mN] = hhmm.split(':').map(Number);
  return h * 60 + m - (hN * 60 + mN);
}

/**
 * Formato resumido de un set de reglas para mostrar en listas.
 * Ej: "Lun-Vie 8:00-17:00", "Mar y Jue 9:00-13:00", "Variable"
 */
export function formatHorarioResumen(reglas: ReglaHorario[]): string {
  if (reglas.length === 0) return 'Sin horarios';

  // Agrupar reglas con misma hora-inicio/fin
  const grupos = new Map<string, number[]>();
  for (const r of reglas) {
    const key = `${r.horaInicio}-${r.horaFin}`;
    const arr = grupos.get(key) ?? [];
    arr.push(r.diaSemana);
    grupos.set(key, arr);
  }

  if (grupos.size > 1) {
    return `${reglas.length} bloques (horario variable)`;
  }

  const [horario, dias] = [...grupos.entries()][0];
  const diasOrdenados = [...new Set(dias)].sort();
  return `${formatDiasRango(diasOrdenados)} ${horario.replace('-', ' a ')}`;
}

const NOMBRES_CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

function formatDiasRango(dias: number[]): string {
  if (dias.length === 0) return '';
  if (dias.length === 7) return 'Todos los días';
  if (dias.length === 1) return NOMBRES_CORTOS[dias[0]];

  // Detecta si es un rango contiguo
  const ordenados = [...dias].sort();
  const esContiguo = ordenados.every(
    (d, i) => i === 0 || d === ordenados[i - 1] + 1,
  );
  if (esContiguo) {
    return `${NOMBRES_CORTOS[ordenados[0]]}-${NOMBRES_CORTOS[ordenados[ordenados.length - 1]]}`;
  }
  return ordenados.map((d) => NOMBRES_CORTOS[d]).join(', ');
}
